import { Capacitor, registerPlugin } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import { Share } from '@capacitor/share'
import { AdMob } from '@capacitor-community/admob'
import type { LifeItem } from '../models'
import type { DailyQuote } from '../quotes'
import { CATEGORY_META } from '../data'
import { fromDateKey, getLunarLabel, toDateKey } from './date'
import { isCompletedOn, occursOnDate } from './recurrence'

const CHANNEL_ID = 'life-calendar-reminders'
const TEST_BANNER_ID = 'ca-app-pub-3940256099942544/9214589741'
const PRODUCTION_BANNER_ID = 'ca-app-pub-1441018945572157/9709703124'
const USE_PRODUCTION_ADS = import.meta.env.MODE === 'android-release'
let adsReady: Promise<boolean> | null = null
let bannerRequestId = 0
let consentRetryAt = 0
let bannerQueue: Promise<unknown> = Promise.resolve()

interface DockedBannerPlugin {
  setVisible(options: { visible: boolean; adId?: string; discard?: boolean }): Promise<void>
}

const DockedBanner = registerPlugin<DockedBannerPlugin>('DockedBanner')

interface LifeWidgetPlugin {
  update(options: { lunar: string; quote: string; lines: string[] }): Promise<void>
}

const LifeWidget = registerPlugin<LifeWidgetPlugin>('LifeWidget')

function notificationId(value: string): number {
  let hash = 0
  for (const character of value) hash = ((hash * 31) + character.charCodeAt(0)) | 0
  return Math.abs(hash || 1)
}

export function isNativeApp(): boolean {
  return Capacitor.isNativePlatform()
}

export async function enableNotifications(items: LifeItem[]): Promise<boolean> {
  if (!isNativeApp()) return false
  const permission = await LocalNotifications.requestPermissions()
  if (permission.display !== 'granted') return false

  await LocalNotifications.createChannel({
    id: CHANNEL_ID,
    name: '생활 일정 알림',
    description: '사용자가 등록한 일정과 할 일을 알려드립니다.',
    importance: 4,
    visibility: 1,
    vibration: true,
  })
  await syncNotifications(items)
  return true
}

export async function disableNotifications(): Promise<void> {
  if (!isNativeApp()) return
  await LocalNotifications.cancelAll()
}

export async function syncNotifications(items: LifeItem[]): Promise<void> {
  if (!isNativeApp()) return
  await LocalNotifications.cancelAll()

  const now = new Date()
  const notifications = []
  for (let offset = 0; offset <= 60 && notifications.length < 64; offset += 1) {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset)
    const dateKey = toDateKey(date)
    for (const item of items) {
      if (!item.time || !occursOnDate(item, date) || isCompletedOn(item, dateKey)) continue
      const [hour, minute] = item.time.split(':').map(Number)
      const at = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute)
      if (at <= now) continue
      notifications.push({
        id: notificationId(`${item.id}:${dateKey}`),
        title: '생활달력 일정 알림',
        body: `${item.time} ${item.title}`,
        schedule: { at, allowWhileIdle: true },
        channelId: CHANNEL_ID,
        isExactNotification: false,
        extra: { itemId: item.id, date: dateKey },
      })
      if (notifications.length >= 64) break
    }
  }

  if (notifications.length > 0) await LocalNotifications.schedule({ notifications })
}

export async function shareLifeItem(item: LifeItem, dateKey: string): Promise<void> {
  const date = fromDateKey(dateKey)
  const category = CATEGORY_META[item.category].label
  const text = [
    `[큰글달력] ${category}`,
    `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일${item.time ? ` ${item.time}` : ''}`,
    item.title,
  ].join('\n')

  if (isNativeApp()) {
    await Share.share({ title: item.title, text, dialogTitle: '일정 공유하기' })
    return
  }
  if (navigator.share) {
    await navigator.share({ title: item.title, text })
    return
  }
  await navigator.clipboard.writeText(text)
  window.alert('일정 내용을 복사했습니다.')
}

export async function shareDailyQuote(quote: DailyQuote): Promise<void> {
  const heading = quote.kind === 'bible' ? '오늘의 말씀' : '오늘의 한마디'
  const text = [`[큰글달력] ${heading}`, `“${quote.text}”`, quote.source].join('\n')
  if (isNativeApp()) {
    await Share.share({ title: heading, text, dialogTitle: `${heading} 공유하기` })
    return
  }
  if (navigator.share) {
    await navigator.share({ title: heading, text })
    return
  }
  await navigator.clipboard.writeText(text)
  window.alert(`${heading}을 복사했습니다.`)
}

export function initializeAds(): Promise<boolean> {
  if (!isNativeApp()) return Promise.resolve(false)
  if (adsReady) return adsReady
  if (Date.now() < consentRetryAt) return Promise.resolve(false)
  adsReady = (async () => {
    await AdMob.initialize({ initializeForTesting: !USE_PRODUCTION_ADS })
    let consent = await AdMob.requestConsentInfo()
    if (!consent.canRequestAds && consent.isConsentFormAvailable) {
      consent = await AdMob.showConsentForm()
    }
    return consent.canRequestAds
  })().catch(() => {
    // Allow recovery on a later foreground/online event after a network failure.
    adsReady = null
    consentRetryAt = Date.now() + 60_000
    return false
  })
  return adsReady
}

function setBannerVisible(visible: boolean, requestId: number, discard = false): Promise<boolean> {
  const operation = bannerQueue.catch(() => undefined).then(async () => {
    if (requestId !== bannerRequestId) return false
    await DockedBanner.setVisible({
      visible,
      ...(discard ? { discard: true } : {}),
      ...(visible ? { adId: USE_PRODUCTION_ADS ? PRODUCTION_BANNER_ID : TEST_BANNER_ID } : {}),
    })
    return visible
  }).catch(() => false)
  bannerQueue = operation
  return operation
}

export async function showCalendarBanner(): Promise<boolean> {
  if (!isNativeApp()) return false
  const requestId = ++bannerRequestId
  if (!(await initializeAds()) || requestId !== bannerRequestId) return false
  return setBannerVisible(true, requestId)
}

export async function hideCalendarBanner(): Promise<void> {
  const requestId = ++bannerRequestId
  if (!isNativeApp()) return
  await setBannerVisible(false, requestId)
}

export async function showAdPrivacyOptions(): Promise<void> {
  if (!isNativeApp()) return
  await AdMob.showPrivacyOptionsForm()
  await setBannerVisible(false, ++bannerRequestId, true)
  // Recheck consent before the next ad request after a privacy choice changes.
  adsReady = null
  consentRetryAt = 0
}

export async function syncWidgetSnapshot(items: LifeItem[], showLunar: boolean, quote: string): Promise<void> {
  if (!isNativeApp()) return
  const today = new Date()
  const lines = items
    .filter((item) => occursOnDate(item, today))
    .sort((left, right) => (left.time ?? '99:99').localeCompare(right.time ?? '99:99'))
    .slice(0, 2)
    .map((item) => `${item.time ? `${item.time} ` : ''}${item.title}`)
  await LifeWidget.update({
    lunar: showLunar ? getLunarLabel(today) : '',
    quote,
    lines,
  })
}
